import { Card, CardContent, Stack, Tooltip, Typography } from '@mui/material'
import React from 'react'

import Icon from 'components/Icon'
import { ServiceReportResponse } from 'types/api/policies/reliability-report'
import { getLightMuiBackgroundColor } from 'utils/colors'
import { formatDateTime } from 'utils/formatDate'

import { useGetService } from '../../../hooks/use-services'
import { formatOwnerDetails } from '../../services/create/CreateServiceStepper'

const TREND_ICON_VALUES = {
  Up: {
    iconName: 'solidArrowTrendUp',
    iconColor: 'success.main'
  },
  Down: {
    iconName: 'solidArrowTrendDown',
    iconColor: 'error.main'
  },
  NoChange: {
    iconName: 'solidDash',
    iconColor: 'warning.main'
  }
}

const DashboardServiceListCard: React.FC<{
  service: ServiceReportResponse
}> = ({ service }) => {
  const {
    inTopList: isInTopList,
    lastModified,
    score,
    serviceName,
    trendType
  } = service
  const { teamName, teamOwnerName, teamOwnerEmail } = service.teamOwner
  const { iconName, iconColor } = TREND_ICON_VALUES[trendType]
  const userDefinedService = useGetService(service.serviceId)?.data

  const { identifier, firstName, lastName } =
    userDefinedService?.serviceOwner || {
      identifier: undefined,
      firstName: undefined,
      lastName: undefined
    }

  return (
    <Card
      data-test-company-dashboard-service-list-card
      sx={(muiTheme) =>
        isInTopList && {
          backgroundColor: getLightMuiBackgroundColor(
            muiTheme.palette.success.light
          )
        }
      }
    >
      <CardContent
        sx={{
          alignItems: 'center',
          display: 'flex',
          paddingRight: 8
        }}
      >
        <Stack flexGrow={2}>
          <Stack alignItems="baseline" direction="row" display="flex" gap={2}>
            <Typography data-test-service-name mb={1} variant="h6">
              {serviceName}
            </Typography>
            {isInTopList && (
              <Tooltip
                arrow
                placement="right"
                title="This Service's Reliability Score is in the top 3 Services in your Organization."
              >
                <Typography color="success.main">
                  <Icon
                    css={{
                      height: 20
                    }}
                    data-test-trophy-icon={service.serviceId}
                    icon="solidTrophy"
                  />
                </Typography>
              </Tooltip>
            )}
          </Stack>
          <Typography data-test-last-modified>
            Last modified: {formatDateTime(lastModified)}
          </Typography>
          {userDefinedService?.serviceOwner ? (
            <Typography data-test-team-information>
              {formatOwnerDetails(identifier, firstName, lastName)}
            </Typography>
          ) : (
            <Typography data-test-team-information>
              {`${teamOwnerName} (${teamOwnerEmail}) in ${teamName}`}
            </Typography>
          )}
        </Stack>
        <Stack direction="row" flexGrow={1} gap={3} justifyContent="flex-end">
          <Typography color={iconColor}>
            <Icon
              css={{ height: 48 }}
              data-test-trend-type={trendType}
              icon={iconName}
            />
          </Typography>
          <Typography data-test-score variant="h4">
            {score}
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  )
}

export default DashboardServiceListCard
